import axios from 'axios'
import CryptoJS from 'crypto-js'
import Cookies from 'js-cookie'
import { getCurrentReturnTo, isKeycloakSsoEnabled } from './helpers/auth/ssoRedirect'
import { clearAuthSession } from './helpers/auth/clearAuthSession'
import { loginWithKeycloak, refreshKeycloakSession } from './services/keycloak'
import store from './store'

const CRYPTO_SECRET_KEY = process.env.VUE_APP_CRYPTO_SECRET_KEY
let redirectingToLogin = false
let refreshPromise = null

function decrypt (cipherText) {
  const bytes = CryptoJS.AES.decrypt(cipherText, CRYPTO_SECRET_KEY)
  return bytes.toString(CryptoJS.enc.Utf8)
}

function encrypt (text) {
  return CryptoJS.AES.encrypt(text, CRYPTO_SECRET_KEY).toString()
}

function getToken () {
  return window.localStorage.getItem('token') || Cookies.get('token')
}

function getRefreshToken () {
  return window.localStorage.getItem('refresh_token') || Cookies.get('refresh_token')
}

function getAccessToken () {
  const token = getToken()
  if (!token) return ''

  return decrypt(token)
}

function setAuthorizationHeader () {
  const accessToken = getAccessToken()
  if (accessToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    return
  }

  delete axios.defaults.headers.common.Authorization
}

function saveToken (key, value) {
  if (!value) {
    window.localStorage.removeItem(key)
    Cookies.remove(key, { path: '/' })
    return
  }

  const encryptedValue = encrypt(value)
  window.localStorage.setItem(key, encryptedValue)
  Cookies.set(key, encryptedValue, { path: '/' })
}

async function refreshSsoSession () {
  const refreshToken = getRefreshToken()
  if (!refreshToken) throw new Error('Missing refresh token')

  const session = await refreshKeycloakSession(decrypt(refreshToken))

  saveToken('token', session.token)
  saveToken('refresh_token', session.refreshToken)
  saveToken('id_token', session.idToken)
  saveToken('keycloak_sid', session.sid)
  saveToken('keycloak_sub', session.sub)
  store.commit('Auth/SET_TOKEN', session.token)
  store.commit('Auth/SET_REFRESH_TOKEN', session.refreshToken)
  store.commit('Auth/SET_ID_TOKEN', session.idToken)

  return session
}

axios.interceptors.request.use(config => {
  const accessToken = getAccessToken()
  config.headers = config.headers || {}

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  } else {
    delete config.headers.Authorization
  }

  return config
})

axios.interceptors.response.use(
  response => response,
  async error => {
    const status = error.response?.status
    const path = window.location.pathname
    const isAuthRoute = path.startsWith('/auth') || path.startsWith('/callback')
    let refreshFailed = false

    if (
      status === 401 &&
      isKeycloakSsoEnabled() &&
      !isAuthRoute &&
      !error.config?._retry &&
      !error.config?.skipAuthRefresh
    ) {
      error.config._retry = true

      try {
        refreshPromise = refreshPromise || refreshSsoSession()
        const session = await refreshPromise
        refreshPromise = null
        error.config.headers = error.config.headers || {}
        error.config.headers.Authorization = `Bearer ${session.token}`
        return axios(error.config)
      } catch (refreshError) {
        refreshPromise = null
        refreshFailed = true
      }
    }

    const shouldRedirectToLogin = isKeycloakSsoEnabled()
      ? (!error.config?._retry || refreshFailed)
      : true

    if (status === 401 && !redirectingToLogin && !isAuthRoute && shouldRedirectToLogin) {
      redirectingToLogin = true
      clearAuthSession()
      if (isKeycloakSsoEnabled()) loginWithKeycloak(getCurrentReturnTo())
      else window.location.replace('/auth1')
    }

    return Promise.reject(error)
  }
)

setAuthorizationHeader()

export default axios
